import { useState, useEffect } from 'react';

const useAudioDurations = (audioUrls = []) => {
  // store durations per-audio URL
  const [durations, setDurations] = useState({});

  // Format duration in seconds to "Xs" format
  const formatAudioDuration = seconds => {
    if (!seconds || isNaN(seconds)) return '0s';
    return `${Math.round(seconds)}s`;
  };

  useEffect(() => {
    if (!audioUrls.length) return;

    const audioRefs = [];
    const newDurations = {};

    const loadAudioDuration = (audioUrl) => {
      if (durations[audioUrl] || newDurations[audioUrl]) return;

      const audio = new Audio();
      
      const handleLoadedMetadata = () => {
        const duration = audio.duration;
        const formattedDuration = formatAudioDuration(duration);
        setDurations(prev => ({
          ...prev,
          [audioUrl]: formattedDuration,
        }));
      };

      const handleError = () => {
        console.error(`Failed to load audio: ${audioUrl}`);
        setDurations(prev => ({
          ...prev,
          [audioUrl]: 'Error',
        }));
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('error', handleError);
      audio.src = audioUrl;
      
      audioRefs.push({
        audio,
        cleanup: () => {
          audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
          audio.removeEventListener('error', handleError);
          audio.src = '';
        }
      });

      newDurations[audioUrl] = true;
    };

    // Load durations for all audio URLs
    audioUrls.forEach(audioUrl => {
      if (audioUrl) {
        loadAudioDuration(audioUrl);
      }
    });

    // Cleanup function
    return () => {
      audioRefs.forEach(ref => {
        ref.cleanup();
      });
    };
  }, [audioUrls.join(','), durations]);

  const getDuration = (audioUrl) => {
    // safe-guard in case durations is not ready
    if (!durations) return 'Audio';
    return durations[audioUrl] || 'Audio';
  };

  return { getDuration, durations };
};

export default useAudioDurations;